import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  GiftOutlined,
  HourglassOutlined,
  RollbackOutlined,
  StopOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { ReactNode } from "react";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

/**
 * Shared notification visuals: the per-type icon badge used by both the
 * drawer's NotificationBlock and the dashboard's compact item. The row
 * surface and the unseen cues (navy accent bar + dot + bold title) live in
 * those two components, which mirror each other.
 */

// Notification type → icon. Clash is the legacy digest; booked..left_waitlist
// are customer self-service events surfaced from the Spott client;
// package_expiring/low_credit are the daily package-alert sweep.
const TYPE_ICONS: Record<string, ReactNode> = {
  clash: <WarningOutlined />,
  booked: <CheckCircleOutlined />,
  waitlisted: <ClockCircleOutlined />,
  cancelled: <CloseCircleOutlined />,
  no_show: <StopOutlined />,
  left_waitlist: <RollbackOutlined />,
  reward_claimed: <GiftOutlined />,
  package_expiring: <HourglassOutlined />,
  low_credit: <CreditCardOutlined />,
  // LEGACY. A void and its undo stopped writing notifications on 2026-10-06
  // (a void is bookkeeping an admin just did on purpose, not something that
  // needs attention; its record lives on the package). The two entries and
  // their notifications.*.header strings stay only so the rows written
  // before that date keep rendering instead of falling back to the clash
  // entry's amber warning treatment. Do not emit these types again.
  package_voided: <StopOutlined />,
  package_void_undone: <UndoOutlined />,
};

// Rounded icon badge (prototype .nicon). Clash keeps the amber alert treatment;
// customer-event icons use a calm neutral badge so only a real conflict reads as
// a warning. Legacy rows (no type) fall back to clash.
export const NotificationIconBadge = ({
  type,
  size = 34,
}: {
  type?: string;
  size?: number;
}) => {
  const resolved = type && TYPE_ICONS[type] ? type : "clash";
  const isClash = resolved === "clash";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size >= 34 ? 10 : 9,
        background: isClash ? colors.amberTint : colors.bgSoft,
        color: isClash ? colors.amberDark : colors.ink2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: textPx(size >= 34 ? 16 : 14),
      }}
    >
      {TYPE_ICONS[resolved]}
    </div>
  );
};
